<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-boxes-stacked me-2" style="color: #0ea5e9;"></i>{{ $t('Material Usage') }}
          </h5>
          <small class="text-muted">{{ $t('Inventory & Stock') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-6">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Total Material Cost') }}</div>
            <div class="fw-bold fs-4 text-primary">{{ formatCurrency(summary.total_cost) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-6">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-3 text-center">
            <div class="text-muted small mb-1">{{ $t('Items Used') }}</div>
            <div class="fw-bold fs-4">{{ summary.items_count || 0 }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Top items by cost chart -->
    <div v-if="topByCost.length" class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold">
          <i class="fa-solid fa-chart-bar me-2 text-primary"></i>{{ $t('Top Materials by Cost') }}
        </h6>
      </div>
      <div class="card-body pt-0">
        <apexchart ref="topChart" type="bar" :height="topChartHeight" :options="topChartOptions" :series="topChartSeries" />
      </div>
    </div>

    <!-- Detail Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold">
          <i class="fa-solid fa-list me-2 text-secondary"></i>{{ $t('Material Usage Detail') }}
        </h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive table-scroll">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">#</th>
                <th>{{ $t('Item') }}</th>
                <th>{{ $t('Unit') }}</th>
                <th class="text-end">{{ $t('Sale') }}</th>
                <th class="text-end">{{ $t('Production') }}</th>
                <th class="text-end">{{ $t('Waste') }}</th>
                <th class="text-end">{{ $t('Internal') }}</th>
                <th class="text-end">{{ $t('Total Qty') }}</th>
                <th class="text-end pe-3">{{ $t('Cost') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!itemsData.length && !loading">
                <td colspan="9" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="(it, i) in itemsData" :key="it.inventory_id || i">
                <td class="ps-3">{{ i + 1 }}</td>
                <td class="fw-medium">{{ it.item }}</td>
                <td>{{ $t(it.unit) }}</td>
                <td class="text-end text-primary">{{ formatQty(it.used_sale) }}</td>
                <td class="text-end text-success">{{ formatQty(it.used_production) }}</td>
                <td class="text-end text-danger">{{ formatQty(it.used_waste) }}</td>
                <td class="text-end text-info">{{ formatQty(it.used_internal) }}</td>
                <td class="text-end fw-semibold">{{ formatQty(it.total_qty) }}</td>
                <td class="text-end pe-3 fw-semibold">{{ formatCurrency(it.cost) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="itemsData.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3" colspan="8">{{ $t('Total') }}</td>
                <td class="text-end pe-3">{{ formatCurrency(summary.total_cost) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportMaterialUsage',
  components: { apexchart: VueApexCharts },
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Material Usage',
      summary: { total_cost: 0, items_count: 0 },
      items: [],
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.materialUsage(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.summary  = payload.summary || { total_cost: 0, items_count: 0 };
      this.items    = payload.items || [];
    },

    formatQty(v) {
      const n = Number(v || 0);
      if (n === 0) return '0';
      // Show integer when clean, up to 4 decimals otherwise (no trailing zeros)
      return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(4)));
    },

    async buildPdfDefinition() {
      await this.$nextTick();
      const chartImg = await this.getChartImage('topChart');
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Material Usage'), style: 'sectionTitle' },
        {
          columns: [
            { text: [{ text: this.$t('Total Material Cost') + '\n', style: 'kpiLabel' }, { text: this.formatCurrency(this.summary.total_cost), style: 'kpiValue' }], width: '*' },
            { text: [{ text: this.$t('Items Used') + '\n', style: 'kpiLabel' }, { text: String(this.summary.items_count || 0), style: 'kpiValue' }], width: '*' },
          ],
          margin: [0, 0, 0, 16],
        },
      ];
      if (chartImg) {
        content.push(
          { text: this.$t('Top Materials by Cost'), style: 'sectionTitle' },
          { image: chartImg, width: 500, margin: [0, 0, 0, 12] },
        );
      }
      content.push(
        { text: this.$t('Material Usage Detail'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: [20, '*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: '#', style: 'tableHeader' },
                { text: this.$t('Item'), style: 'tableHeader' },
                { text: this.$t('Unit'), style: 'tableHeader' },
                { text: this.$t('Sale'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Production'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Waste'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Internal'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Total Qty'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Cost'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.itemsData.map((it, i) => [
                { text: String(i + 1), style: 'tableCell' },
                { text: it.item, style: 'tableCell' },
                { text: it.unit, style: 'tableCell' },
                { text: this.formatQty(it.used_sale), style: 'tableCell', alignment: 'right', color: '#3b82f6' },
                { text: this.formatQty(it.used_production), style: 'tableCell', alignment: 'right', color: '#22c55e' },
                { text: this.formatQty(it.used_waste), style: 'tableCell', alignment: 'right', color: '#ef4444' },
                { text: this.formatQty(it.used_internal), style: 'tableCell', alignment: 'right', color: '#06b6d4' },
                { text: this.formatQty(it.total_qty), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(it.cost), style: 'tableCell', alignment: 'right' },
              ]),
              [
                { text: this.$t('Total'), style: 'tableHeader', colSpan: 8 }, {}, {}, {}, {}, {}, {}, {},
                { text: this.formatCurrencyRaw(this.summary.total_cost), style: 'tableHeader', alignment: 'right' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },
      );
      return { content };
    },
  },
  computed: {
    itemsData() {
      return this.items || [];
    },
    topByCost() {
      return this.itemsData.slice(0, 10);
    },
    topChartHeight() {
      return Math.max(260, 32 * this.topByCost.length + 40);
    },
    topChartSeries() {
      return [{ name: this.$t('Cost'), data: this.topByCost.map(it => Number(it.cost) || 0) }];
    },
    topChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Sarabun, sans-serif' },
        plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: '60%' } },
        xaxis: {
          categories: this.topByCost.map(it => it.item),
          labels: { formatter: v => this.formatCompact(v) },
        },
        colors: ['#0ea5e9'],
        dataLabels: {
          enabled: true,
          style: { fontSize: '11px' },
          formatter: v => this.formatCurrency(v),
        },
        tooltip: { y: { formatter: v => this.formatCurrency(v) } },
        grid: { borderColor: '#f1f5f9' },
      };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}

/* Sticky thead + tfoot when the detail table grows tall */
.table-scroll {
  max-height: 60vh;
  overflow-y: auto;
}
.table-scroll thead.table-light th {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 2;
}
.table-scroll tfoot.table-light td {
  position: sticky;
  bottom: 0;
  background-color: #f8f9fa;
  z-index: 2;
}
</style>
